import React, { ReactElement, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';

import Subtext from '~/components/titles/Subtext';
import Button from '~/components/buttons/Button';
import Announcement from '~/components/Announcement';
import Gap from '~/components/flow/Gap';
import COLORS from '~/constants/colors';
import Break from '~/components/flow/Break';
import { FONT_WEIGHTS } from '~/constants/typography';
import VARIANTS from '~/constants/variants';

import { decode } from '../../../services';
import { validateImageDims } from '../../../utils';
import {
  MIME_TYPES,
  MIN_IMAGE_SIZE,
  MAX_IMAGE_SIZE,
} from '../../../constants/media';
import Display from './Display';

const DEFAULT_DECODE_INFO = {
  encoded: undefined as File | undefined,
  original: undefined as File | undefined,
};

function Form(): ReactElement {
  const [fail, setFail] = useState('');
  const [message, setMessage] = useState('');
  const fileInputRefE = useRef<HTMLInputElement>(null);
  const fileInputRefO = useRef<HTMLInputElement>(null);

  // Form
  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    reset,
    resetField,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: DEFAULT_DECODE_INFO });
  const validates = {
    E: async (file) => {
      if (!file.type.startsWith('image')) return false;

      const hasCorrectDims = await validateImageDims(file);

      return hasCorrectDims;
    },
    O: async (file) => {
      if (!file) return true;
      else if (!Object.values(MIME_TYPES).includes(file.type)) return false;

      const hasCorrectDims = await validateImageDims(file);

      return hasCorrectDims;
    },
  };
  const opts = {
    E: { required: true, validate: validates.E },
    O: { validate: validates.O },
  };
  const fields = { E: 'encoded' as const, O: 'original' as const };

  // Rendering
  const defaultPBLabels = {
    E: 'Select leaked image with encoding',
    O: 'Select original image without encoding',
  };
  const defaultPickeds = { E: false, O: false };
  const [pBLabels, setPBLabels] = useState(defaultPBLabels);
  const [pickeds, setPickeds] = useState(defaultPickeds);

  // Handlers
  const getStartPicking = (fileInputRef) => () => fileInputRef.current?.click();
  const getHandlePicking = (fileInputRef, k) => async () => {
    const file = fileInputRef.current?.files?.[0];
    const canDecode = file && (await validates[k](file));
    const field = fields[k];

    if (!canDecode) {
      setPBLabels({ ...pBLabels, [k]: defaultPBLabels[k] });
      setPickeds({ ...pickeds, [k]: false });
      setError(field, { type: 'manual' });
      resetField(field, { keepError: true });
    } else {
      setPBLabels({ ...pBLabels, [k]: file.name });
      setPickeds({ ...pickeds, [k]: true });
      clearErrors(field);
      setValue(field, file);
    }
  };
  const onDecode = (decodeInfo) => {
    return new Promise<void>((resolve) => {
      setFail('');
      decode(decodeInfo.encoded, decodeInfo.original)
        .then((secret) => {
          setMessage(secret.message);
          setPBLabels(defaultPBLabels);
          setPickeds(defaultPickeds);
          reset();
          if (fileInputRefE.current) fileInputRefE.current.value = '';
          if (fileInputRefO.current) fileInputRefO.current.value = '';
        })
        .catch((e) => {
          setFail(e.response.data.error.message);
          resolve();
        });
    });
  };

  // Styling
  const formStyle = {
    display: 'inline-block',
    width: '90%',
  };
  const decodeButtonStyle = {
    width: '63%',
  };
  const announcementStyle = {
    fontWeight: FONT_WEIGHTS.bold,
    backgroundColor: COLORS.cashmere,
  };
  const pBStyle0 = {
    width: 'calc(100% + 0.8em)',
    margin: '0 -0.4em',
    color: COLORS.doveGray,
    borderColor: COLORS.waikawaGray,
    backgroundColor: COLORS.white75,
    fontWeight: FONT_WEIGHTS.light,
    textAlign: 'left' as const,
  };
  const pBStyle1 = {
    ...pBStyle0,
    backgroundColor: COLORS.kournikova,
    fontWeight: FONT_WEIGHTS.normal,
  };

  return (
    <>
      {fail && (
        <>
          <Announcement style={announcementStyle}>{fail}</Announcement>
          <Break />
        </>
      )}
      <form style={formStyle}>
        <div>
          <Button
            variant={VARIANTS.neutral}
            label={pBLabels.E}
            onClick={getStartPicking(fileInputRefE)}
            onTouchEnd={getStartPicking(fileInputRefE)}
            style={pickeds.E ? pBStyle1 : pBStyle0}
          />
          <input
            {...register(fields.E, opts.E)}
            type="file"
            hidden
            ref={fileInputRefE}
            onChange={getHandlePicking(fileInputRefE, 'E')}
          />
          {errors.encoded && (
            <Subtext color={COLORS.mandy}>Invalid format or dimensions</Subtext>
          )}
        </div>
        <Gap />
        <Subtext>
          If decoding fails, you may still be able to retrieve the identifying
          message by manually aligning the leaked image with your original image
          in an editor. You want the leaked image you input to resemble an
          encoded image received from Auri as much as possible. Dimensions of
          supplied images must be between <b>{MIN_IMAGE_SIZE}</b> and{' '}
          <b>{MAX_IMAGE_SIZE}</b> pixels.
        </Subtext>
        <Break />
        <div>
          <Button
            variant={VARIANTS.neutral}
            label={pBLabels.O}
            onClick={getStartPicking(fileInputRefO)}
            onTouchEnd={getStartPicking(fileInputRefO)}
            style={pickeds.O ? pBStyle1 : pBStyle0}
          />
          <input
            {...register(fields.O, opts.O)}
            type="file"
            hidden
            ref={fileInputRefO}
            onChange={getHandlePicking(fileInputRefO, 'O')}
          />
          {errors.original && (
            <Subtext color={COLORS.mandy}>Invalid format or dimensions</Subtext>
          )}
        </div>
        <Break />
        <Button
          label="Decode"
          loadingLabel="Decoding..."
          loading={isSubmitting}
          onClick={handleSubmit(onDecode)}
          onTouchEnd={handleSubmit(onDecode)}
          style={decodeButtonStyle}
        />
      </form>
      <Display message={message} setMessage={setMessage} />
    </>
  );
}

export default Form;
